'use client';

import type { AdvisorStagedClient } from 'src/features/advisor-book-onboarding/utils/staged-book';
import { useState } from 'react';
import {
  REMOVE_ADVISOR_ONBOARDING_STAGED_CO_CLIENT_MUTATION,
  REMOVE_ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_MUTATION,
  removeStagedCoClientSucceeded,
  removeStagedHouseholdSucceeded,
  splitStagedClientFullName,
  UPDATE_ADVISOR_ONBOARDING_STAGED_CLIENT_MUTATION,
  UPDATE_ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_MUTATION,
  updateStagedClientSucceeded,
  updateStagedHouseholdSucceeded,
} from 'src/features/advisor-book-onboarding/api/advisor-book-onboarding-gql';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

type UseStagedBookMutationsParams = {
  readonly refetchStagedBook: () => Promise<void>;
};

type UpdateStagedClientFieldParams = {
  readonly clientId: string;
  readonly field: 'email' | 'phone';
  readonly value: string;
};

type UpdateStagedClientNameParams = {
  readonly clientId: string;
  readonly fullName: string;
  readonly originalFirstName?: string | null;
  readonly originalLastName?: string | null;
};

type UpdateStagedHouseholdNameParams = {
  readonly householdId: string;
  readonly householdName: string;
};

type UseStagedBookMutationsResult = {
  readonly savingClientId: string | undefined;
  readonly savingHouseholdId: string | undefined;
  readonly removingHouseholdId: string | undefined;
  readonly removingCoClientId: string | undefined;
  readonly updateStagedClientField: (params: UpdateStagedClientFieldParams) => Promise<boolean>;
  readonly updateStagedClientName: (params: UpdateStagedClientNameParams) => Promise<boolean>;
  readonly updateStagedHouseholdName: (params: UpdateStagedHouseholdNameParams) => Promise<boolean>;
  readonly removeStagedHousehold: (householdId: string) => Promise<boolean>;
  readonly removeStagedCoClient: (clientId: string) => Promise<boolean>;
};

export function useStagedBookMutations({
  refetchStagedBook,
}: UseStagedBookMutationsParams): UseStagedBookMutationsResult {
  const [savingClientId, setSavingClientId] = useState<string | undefined>(undefined);
  const [savingHouseholdId, setSavingHouseholdId] = useState<string | undefined>(undefined);
  const [removingHouseholdId, setRemovingHouseholdId] = useState<string | undefined>(undefined);
  const [removingCoClientId, setRemovingCoClientId] = useState<string | undefined>(undefined);

  const [updateStagedClient] = useMutationWithErrorLogging(UPDATE_ADVISOR_ONBOARDING_STAGED_CLIENT_MUTATION);
  const [updateStagedHousehold] = useMutationWithErrorLogging(UPDATE_ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_MUTATION);
  const [removeStagedHouseholdMut] = useMutationWithErrorLogging(REMOVE_ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_MUTATION);
  const [removeStagedCoClientMut] = useMutationWithErrorLogging(REMOVE_ADVISOR_ONBOARDING_STAGED_CO_CLIENT_MUTATION);

  async function updateStagedClientField({ clientId, field, value }: UpdateStagedClientFieldParams): Promise<boolean> {
    const input = field === 'email' ? { id: clientId, email: value } : { id: clientId, phone: value };

    setSavingClientId(clientId);
    try {
      const result = await updateStagedClient({ variables: { input } });

      if (!updateStagedClientSucceeded(result.data)) {
        return false;
      }

      await refetchStagedBook();
      return true;
    } catch (mutationError) {
      handleMutationError(mutationError, 'Failed to update contact info, please try again');
      return false;
    } finally {
      setSavingClientId((current) => (current === clientId ? undefined : current));
    }
  }

  async function updateStagedClientName({
    clientId,
    fullName,
    originalFirstName,
    originalLastName,
  }: UpdateStagedClientNameParams): Promise<boolean> {
    const { firstName, lastName } = splitStagedClientFullName({
      fullName,
      originalFirstName: originalFirstName ?? null,
      originalLastName: originalLastName ?? null,
    });

    setSavingClientId(clientId);
    try {
      const result = await updateStagedClient({
        variables: { input: { id: clientId, firstName, lastName } },
      });

      if (!updateStagedClientSucceeded(result.data)) {
        return false;
      }

      await refetchStagedBook();
      return true;
    } catch (mutationError) {
      handleMutationError(mutationError, 'Failed to update contact info, please try again');
      return false;
    } finally {
      setSavingClientId((current) => (current === clientId ? undefined : current));
    }
  }

  async function updateStagedHouseholdName({
    householdId,
    householdName,
  }: UpdateStagedHouseholdNameParams): Promise<boolean> {
    setSavingHouseholdId(householdId);
    try {
      const result = await updateStagedHousehold({
        variables: { input: { id: householdId, householdName } },
      });

      if (!updateStagedHouseholdSucceeded(result.data)) {
        return false;
      }

      await refetchStagedBook();
      return true;
    } finally {
      setSavingHouseholdId((current) => (current === householdId ? undefined : current));
    }
  }

  async function removeStagedHousehold(householdId: string): Promise<boolean> {
    setRemovingHouseholdId(householdId);
    try {
      const result = await removeStagedHouseholdMut({
        variables: { input: { id: householdId } },
      });

      if (!removeStagedHouseholdSucceeded(result.data)) {
        return false;
      }

      await refetchStagedBook();
      return true;
    } finally {
      setRemovingHouseholdId((current) => (current === householdId ? undefined : current));
    }
  }

  async function removeStagedCoClient(clientId: string): Promise<boolean> {
    setRemovingCoClientId(clientId);
    try {
      const result = await removeStagedCoClientMut({
        variables: { input: { id: clientId } },
      });

      if (!removeStagedCoClientSucceeded(result.data)) {
        return false;
      }

      await refetchStagedBook();
      return true;
    } finally {
      setRemovingCoClientId((current) => (current === clientId ? undefined : current));
    }
  }

  return {
    savingClientId,
    savingHouseholdId,
    removingHouseholdId,
    removingCoClientId,
    updateStagedClientField,
    updateStagedClientName,
    updateStagedHouseholdName,
    removeStagedHousehold,
    removeStagedCoClient,
  };
}

export function clientNeedsNameNormalize(client: AdvisorStagedClient): boolean {
  const fullName = [client.firstName, client.lastName]
    .filter((part) => part !== null && part !== '')
    .join(' ')
    .trim();
  const parts = fullName.split(/\s+/).filter((part) => part !== '');
  if (parts.length < 2) {
    return false;
  }

  const lastBlank = (client.lastName?.trim() ?? '') === '';
  const firstBlank = (client.firstName?.trim() ?? '') === '';
  const hasNameImportError =
    client.importErrors['first_name'] !== undefined || client.importErrors['last_name'] !== undefined;

  return hasNameImportError || lastBlank || firstBlank;
}
